import type { CSSProperties } from 'react'

import { useRef } from 'react'
import { usePointerParallax } from '@/hooks/use-pointer-parallax'
import { Landscape } from '@/components/generals/landscape'
import { PartnerWordmark } from '@/components/generals/partner-wordmark'
import { PillButton } from '@/components/generals/pill-button'
import { Reveal } from '@/components/generals/reveal'
import { SplitHeading } from '@/components/generals/split-heading'
import { TextLink } from '@/components/generals/text-link'
import { HeroGrass } from '@/components/landing/home/hero-grass'
import { HeroPortraitCard } from '@/components/landing/home/hero-portrait-card'
import { HERO_PARTNERS } from '@/constants/partners'
import { LANDSCAPES } from '@/lib/visual'

export const Hero = () => {
    const sectionRef = useRef<HTMLElement>(null)

    usePointerParallax(sectionRef)

    return (
        <section ref={sectionRef} id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden bg-black'>
            <div aria-hidden='true' className='absolute inset-0 overflow-hidden'>
                <div
                    className='motion-parallax absolute -inset-[5%] will-change-transform'
                    style={{ transform: 'translate3d(calc(var(--mx, 0) * -14px), calc(var(--my, 0) * -9px), 0)' } as CSSProperties}
                >
                    <div className='absolute inset-0 animate-[fade-in_2.4s_var(--ease-cinema)_both]'>
                        <Landscape src='/images/uploads/background.png' priority fade='none' tint={false} position='center 55%' imageClassName='scale-[1.04] brightness-[.85]' />
                    </div>
                </div>

                <div className='motion-haze absolute inset-0 bg-[radial-gradient(55%_42%_at_50%_40%,rgba(246,226,158,0.32),rgba(246,226,158,0)_70%)] mix-blend-screen' />

                <div className='absolute inset-x-0 top-0 h-[100svh] min-h-[700px]'>
                    <HeroGrass />
                </div>

                <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.62)_0%,rgba(0,0,0,0.1)_22%,rgba(0,0,0,0)_42%,rgba(0,0,0,0.55)_66%,rgba(0,0,0,0.92)_80%,#000_90%)]' />
                <div className='absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_35%,rgba(0,0,0,0)_45%,rgba(0,0,0,0.55)_100%)]' />
            </div>

            <div className='shell relative z-10 flex flex-col items-center pt-[clamp(8.5rem,25svh,15rem)] text-center'>
                <Reveal trigger='mount' delay={150} y={12}>
                    <p className='flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.34em] text-cream/70'>
                        <span aria-hidden='true' className='size-1 rounded-full bg-citron shadow-[0_0_12px_2px_rgba(226,243,122,0.7)]' />
                        Personal finance, guided by AI
                    </p>
                </Reveal>

                <SplitHeading
                    as='h1'
                    id='hero-title'
                    trigger='mount'
                    delay={300}
                    stagger={90}
                    serifDelay={340}
                    text={'Your money, finally\n*in clear view*'}
                    className='mt-7 max-w-5xl text-balance text-[2.8rem] font-normal leading-[0.98] tracking-[-0.05em] text-cream drop-shadow-[0_8px_40px_rgba(0,0,0,0.45)] sm:text-7xl md:text-[5.6rem] lg:text-[6.5rem]'
                />

                <Reveal trigger='mount' delay={1350} y={16} className='mt-10'>
                    <PillButton href='/login?mode=signup' size='md'>
                        Try Verdane for free
                    </PillButton>
                </Reveal>
            </div>

            <div className='shell relative z-10 mt-16 grid items-end gap-14 pb-28 sm:mt-20 lg:grid-cols-[1fr_auto_1fr] lg:gap-12 lg:pb-36'>
                <Reveal trigger='mount' delay={1650} className='order-2 text-center lg:order-1 lg:pb-6 lg:text-left'>
                    <p className='text-[11px] font-medium uppercase tracking-[0.32em] text-cream/50'>
                        In good company
                    </p>

                    <ul className='mx-auto mt-6 flex max-w-sm flex-wrap items-center justify-center gap-x-7 gap-y-5 text-[15px] lg:mx-0 lg:max-w-[360px] lg:justify-start'>
                        {HERO_PARTNERS.map(id => (
                            <li key={id}>
                                <PartnerWordmark id={id} />
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <HeroPortraitCard className='order-1 mx-auto lg:order-2' />

                <Reveal trigger='mount' delay={1800} className='order-3 mx-auto max-w-sm text-center lg:mr-0 lg:pb-6 lg:text-left'>
                    <p className='text-[15px] leading-relaxed text-cream/70'>
                        Verdane gathers your accounts, goals and bills into one quiet place. A personal AI advisor learns the rhythm of your life, answers in plain language and helps you choose the next step with confidence.
                    </p>

                    <TextLink href='/what-we-do' className='mt-5 inline-block'>
                        See how it works
                    </TextLink>
                </Reveal>
            </div>
        </section>
    )
}
